// /about - what this system is, what is built and what is not, how signatures and
// work eligibility work. Static: readable without a demo session.
import type { ReactNode } from 'react'
import { RuleRefs } from '@/components/evidence'
import { Banner, Card, PageHeader, SectionTitle } from '@/components/ui'
import { SYNTHETIC_NOTICE } from '@/domain/catalogue'
import { LinkButton } from './LinkButton'

const BUILT: ReactNode[] = [
  'Class B (blue) permits, from draft to archive',
  'Application, submission and the permit meeting review, including the urgent route',
  'Agreement by other affected custodians',
  'Supporting documents: recorded by the PA, verified by the RS',
  'Isolation on the application: kind, reason, planned period and whether the Isolation Certificate (F08) is completed; the certificate recorded and verified before authorisation; what became of the isolation recorded at closeout (paper forms F08 and F09)',
  'Authorisation by the RS, then a one-time approval by the Operations Manager before any work starts',
  'Daily cycle: request, RS pre-issue, gas tests, RS validation (16 checks; the same or a second RS), toolbox talk with crew signatures, PH acceptance',
  'Stops: report, PH declaration and RS confirmation; scope-change reports',
  'PH cancellation and RS closeout, then the archive record',
  'Live board, printable permit and the rules page',
]

const NOT_BUILT: string[] = [
  'Class A (red) permits',
  'Task SOP / Notification of Work',
  'No Permit Job',
  'Point-by-point isolation certificate workflow in the app (design, apply, prove, de-isolate for test, EPI review): on paper forms F08 and F09 for now',
  'Confined-space entry',
  'Excavation',
  'Vehicle entry',
  'Hot work',
  'Level 2 Job HSE plans',
  'Handover between Permit Holders',
  'Suspension by alarm or emergency',
  'Remote suspension',
  'Importing paper permits',
]

function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-sm text-ink">
      {items.map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </ul>
  )
}

export function AboutPage() {
  return (
    <div className="space-y-4">
      <PageHeader title="About this system" subtitle="BadrEOR electronic Permit to Work - synthetic demonstration" />
      <Banner tone="synthetic" title={SYNTHETIC_NOTICE} />

      <Card>
        <SectionTitle>What this is</SectionTitle>
        <div className="space-y-2 text-sm text-ink">
          <p>This is BadrEOR&apos;s electronic Permit to Work (ePTW), running as a synthetic demonstration.</p>
          <p>
            It follows a draft BadrEOR rulebook that uses PDO SP-2329 Rev 2.0 (June 2020) as its reference. The rulebook is{' '}
            <span className="font-semibold">not approved BadrEOR policy</span>: every value is marked pending approval, synthetic
            or unresolved.
          </p>
          <p>
            The people, companies, sites, equipment, licences and gas instruments are fictitious. This demonstration cannot issue a
            real permit to work.
          </p>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <SectionTitle>What is built</SectionTitle>
          <Bullets items={BUILT} />
        </Card>
        <Card>
          <SectionTitle>Not built yet</SectionTitle>
          <p className="mb-2 text-sm text-ink">
            These are not available. A permit that needs one of them is refused with the reason and its rule reference; it is never
            silently allowed.
          </p>
          <Bullets items={NOT_BUILT} />
        </Card>
      </div>

      <Card>
        <SectionTitle>How signatures work</SectionTitle>
        <Bullets
          items={[
            <>
              Each person signs with their own sign-in. The server records who signed, in which role, under which licence, and the
              time from its own clock. The app never sends a name or a time as proof.
              <RuleRefs ruleIds={['SYS-005']} />
            </>,
            'Every signing step says what you are declaring. The server keeps a fingerprint (digest) of exactly what was signed.',
            <>
              Signed records are never edited. A correction is a new record, and the history keeps both.
              <RuleRefs ruleIds={['SYS-010']} />
            </>,
            <>
              The server checks licences, their scope and expiry, and that one person does not hold two roles that must stay
              separate on the same permit. For example, the RS cannot authorise a permit they applied for, will hold or will work
              on.
              <RuleRefs ruleIds={['CORE-049', 'CORE-075']} />
            </>,
          ]}
        />
      </Card>

      <Card>
        <SectionTitle>How &quot;work may proceed&quot; is decided</SectionTitle>
        <Bullets
          items={[
            'Nothing stores "active". The server works it out each time a permit is read; an open permit page re-checks every 30 seconds.',
            "It checks that the permit is authorised and inside its authorisation period; the Operations Manager has approved the authorisation (where the policy requires it); today's cycle is validated by an RS, accepted by the PH and inside its validation window; required gas tests are acceptable; every crew member signed the toolbox talk; no stop is open; licences are still current; supporting documents are still verified; and every rule value it needs is usable.",
            'If anything fails, the permit shows "Work must not proceed" with each reason and its rule ID. An unresolved rule value blocks the step it governs.',
            'This is a check of the records. It never replaces the Permit Holder at the worksite or a safe site.',
            <>
              A screen that has lost its connection says so and shows when it was last loaded. A cached screen is never a current
              authorisation.
              <RuleRefs ruleIds={['SYS-009']} />
            </>,
          ]}
        />
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <SectionTitle>Rules</SectionTitle>
          <p className="mb-3 text-sm text-ink">
            The Rules page lists every rule value the server uses, with its status and its source references. It needs a demo
            session.
          </p>
          <LinkButton to="/policy" variant="secondary">
            Open the rules
          </LinkButton>
        </Card>
        <Card>
          <SectionTitle>Contact</SectionTitle>
          <p className="text-sm text-ink">
            Questions or concerns about this system: <span className="font-semibold">BadrEOR QHSE</span>.
          </p>
        </Card>
      </div>
    </div>
  )
}
